import { isInPast } from '@seen/core/dates'
import type { TaskAssignedInboxItem } from '@seen/core/models/inbox'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { cn } from '@seen/ui'
import type { JSX, ReactNode } from 'react'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowAvatar } from '#src/pages/inbox/row/InboxRowAvatar.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowIcon } from '#src/pages/inbox/row/InboxRowIcon.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export interface TaskAssignedInboxItemRowProps {
  item: TaskAssignedInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function TaskAssignedInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: TaskAssignedInboxItemRowProps): ReactNode {
  const { data: task, isLoading: isLoadingTask } = useRead(
    StandardTask,
    item.task
  )
  const { data: practitioner } = useRead(ExtendedPractitioner, item.assigner)
  const { data: patient } = useRead(Participant, task?.participant)
  const { data: order } = useRead(
    Order,
    isOfType(task?.focus, 'ServiceRequest') ? task.focus : undefined
  )
  const actionUrl = task?.actionUrl ?? item.actionUrl

  const orderTitle = getOrderTitle(order)
  const isOverdue =
    !!task?.dueOn && !task.isCompletedOrCancelled && isInPast(task.dueOn)

  return (
    <InboxRow
      to={actionUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
      ghost={!!task?.isCompletedOrCancelled}
    >
      <InboxRowAvatar
        profile={item.assigner}
        icon={
          <InboxRowIcon>
            <IconSwitch />
          </InboxRowIcon>
        }
      />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          isLoading={isLoadingTask}
          badges={
            <>
              {patient && (
                <ParticipantTaskRowBadge
                  className="[&>button]:h-[1.55rem] [&>button]:py-0"
                  participant={patient.reference}
                />
              )}
              {isOfType(task?.focus, 'ServiceRequest') && order && (
                <RowBadge
                  loading={!order}
                  icon={
                    <IconOrderStatus
                      status={order?.status}
                      isSubmitting={order?.isSubmitting}
                      className="size-3.5"
                    />
                  }
                >
                  {order?.seenIdentifier} {!!orderTitle && orderTitle}
                </RowBadge>
              )}
            </>
          }
        >
          {task?.priority && (
            <TaskPriorityIcon
              priority={task.priority}
              isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
              className={cn('-mt-0.75 mr-0.75 inline')}
            />
          )}
          {task?.state && (
            <IconTaskState
              state={task.state}
              className="-mt-0.75 mr-0.75 inline"
            />
          )}
          <span>{extractTextFromHTML(task?.title ?? '[deleted]')}</span>
        </InboxRowText>
        <InboxRowSubtext>
          {task?.isCompletedOrCancelled ? (
            task.isDone ? (
              'Completed'
            ) : (
              'Canceled'
            )
          ) : (
            <>
              {isOverdue && (
                <span className="font-medium text-red-600">
                  Overdue &middot;{' '}
                </span>
              )}
              {!!practitioner &&
                `${practitioner.firstName} assigned the task to you`}
              {!practitioner && 'The task was assigned to you'}
            </>
          )}
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}

function IconSwitch({ className }: { className?: string }): JSX.Element {
  // Icon from linear.app
  return (
    <svg
      className={className}
      width="11"
      height="11"
      viewBox="0 0 16 16"
      fill="lch(90.65 1.35 272)"
      role="img"
      focusable="false"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9.873 8.358a.429.429 0 0 1-.429-.429l.002-1.267c-1.697-.16-5.734-.654-5.734-.948 0-.223 1.91-.569 5.732-1.038V3.43a.428.428 0 0 1 .709-.325L12.9 5.467l.03.03c.103.12.089.3-.03.403l-2.749 2.355a.429.429 0 0 1-.279.103ZM6.452 12.85a.429.429 0 0 1-.605.045L3.1 10.533a.286.286 0 0 1 0-.433l2.749-2.355a.429.429 0 0 1 .708.326l-.002 1.267c1.697.16 5.734.654 5.734.948 0 .223-1.91.569-5.732 1.038v1.248a.429.429 0 0 1-.104.279Z" />
    </svg>
  )
}
